import { DigitalTwinTranslations, useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import type { BidTypeName, ElplanInfoTagForDate, ElplanPartition } from 'api/elplan/elplan.types'
import { useLatestOptJobStatus } from 'api/optJobs/optJobs.api'
import { useUiConfigById } from 'api/uiConfig/uiConfig.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import { type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import { OptimizeButton } from 'ui/components/OptimizeFormProvider/components/OptimizeButton/OptimizeButton'
import UiConfig from 'ui/uiConfig/UiConfig'
import UiConfigComponent from 'ui/uiConfig/UiConfigComponent'
import Datetime from 'utils/datetime/datetime'
import { translateKeyWithSpecificTranslations } from 'utils/stringUtils'
import { useSnapshot } from 'valtio'
import {
  buildRegularPlanSubtypeAliases,
  buildScenarioResultAliases,
  getScenarioBidEditorPartitionId,
  getScenarioOptimizationDisplayName,
  getScenarioOptimizationSubtypes,
} from 'views/DayAheadTradingView/DayAheadTradingView.helper'
import type {
  FormattedBidWithIdAndTitle,
  RenderableScenarioPartitionResolution,
} from 'views/DayAheadTradingView/DayAheadTradingView.types'
import { DayAheadBidEditor } from 'views/ElectricityTradingView/components/shared/DayAheadBidEditor/DayAheadBidEditor'
import { DayAheadBidInputSurface } from '../../DayAheadBidTable/DayAheadBidInputSurface'
import type { DayAheadBidTableResetController } from '../../DayAheadBidTable/DayAheadBidTable.types'
import type { ElectricityPriceScenario } from '../ElectricityPriceScenarioModal/types'
import { isSpotPriceSettledForDate } from '../spotPriceSettlement.helper'
import styles from './ElectricityPriceScenarioVisualization.module.less'
import { ScenarioOptimizationPeriodInfo } from './ScenarioOptimizationPeriodInfo/ScenarioOptimizationPeriodInfo'
import { useDayAheadScenarioOptimization } from './useDayAheadScenarioOptimization'

type ElectricityPriceScenarioVisualizationProps = {
  scenario: ElectricityPriceScenario
  tableContextKey: string
  partitions: ElplanPartition[]
  partitionResolution: RenderableScenarioPartitionResolution
  datasetStartTime: ISODateTime
  datasetEndTime: ISODateTime
  translations?: DigitalTwinTranslations | undefined
  disabledBidButtons?: boolean
  tagsForDate?: ElplanInfoTagForDate[]
  bidInputCurrencies?: { block: string; profile: string }
  requireTagForBlockBids?: boolean
  tableResetController: DayAheadBidTableResetController
  setTypeOfBidToCopy: (bidType: BidTypeName, partitionId?: number) => void
  setCurrentBlockBid: (bid: FormattedBidWithIdAndTitle) => void
  setCurrentProfileBid: (bid: FormattedBidWithIdAndTitle) => void
}

export default function ElectricityPriceScenarioVisualization({
  scenario,
  tableContextKey,
  partitions,
  partitionResolution,
  datasetStartTime,
  datasetEndTime,
  translations,
  disabledBidButtons,
  tagsForDate,
  bidInputCurrencies,
  requireTagForBlockBids,
  tableResetController,
  setTypeOfBidToCopy,
  setCurrentBlockBid,
  setCurrentProfileBid,
}: ElectricityPriceScenarioVisualizationProps): ReactElement {
  const {
    hide_optimize_button: hideOptimizeButton,
    scenario_name: scenarioName,
    ui_config_id: uiConfigId,
    uiconfig_override_alias: uiConfigOverrideAlias,
  } = scenario
  const { systemId, activeSystem } = useAuth()
  useUiConfigById(uiConfigId)
  const { idToUiConfig } = useSnapshot(uiConfigStore)
  const { t } = useTranslation()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)

  const hasEditElplanAccess = useHasPermission('change_elplan')
  const optimizationSubtypes = getScenarioOptimizationSubtypes(scenario, partitionResolution)
  const { data: latestFinishedOptJobs, isSuccess: finishedOptJobsLoaded } = useLatestOptJobStatus({
    type: 'electricity_price_scenario',
    status: 'Finished',
    subtypes: optimizationSubtypes ?? [],
  })
  const { createOptimization, creatingOptimization, optimizationTemporarilyDisabled } =
    useDayAheadScenarioOptimization(optimizationSubtypes)

  const translatedScenarioName = translateKeyWithSpecificTranslations(scenarioName, digitalTwin?.translations)
  const scenarioOptimizationDisplayName = getScenarioOptimizationDisplayName(
    translatedScenarioName,
    partitionResolution
  )

  const optimizeButtonLabel =
    optimizationSubtypes === null ? t('Optimize all scenarios') : `${t('Optimize')} ${scenarioOptimizationDisplayName}`

  const spotPriceSettled = isSpotPriceSettledForDate(datasetStartTime, Datetime.getISONow(0, false))

  const translatedCardTitle = useMemo(() => {
    return translateKeyWithSpecificTranslations(uiConfigOverrideAlias.scenario_card_title as string, translations)
  }, [uiConfigOverrideAlias.scenario_card_title, translations])

  const resultAliases = buildScenarioResultAliases({
    scenario,
    partitions,
    partitionResolution,
    translatedCardTitle,
    createdAt: latestFinishedOptJobs?.[0]?.created_at ?? null,
  })
  const regularPlanSubtypeAliases = buildRegularPlanSubtypeAliases(partitions, partitionResolution)
  const bidEditorPartitionId = getScenarioBidEditorPartitionId(partitionResolution)

  return (
    <>
      {!hideOptimizeButton && (
        <div className={styles.ElectricityPriceScenarioVisualization_OptimizaButtonContainer}>
          <div className="flex w-full items-center justify-end">
            <OptimizeButton
              disabled={!hasEditElplanAccess || optimizationTemporarilyDisabled || spotPriceSettled}
              type="electricity_price_scenario"
              optimizeLabel={!hasEditElplanAccess ? t('You do not have permission to optimize') : optimizeButtonLabel}
              onClick={createOptimization}
              isValid={true}
              subtypes={optimizationSubtypes ?? []}
              creatingOptJob={creatingOptimization}
            />
          </div>
          {spotPriceSettled && (
            <div className="flex w-full justify-end">
              <InfoBanner
                text={t(
                  "The spot price for this date has already been settled, running a Day-Ahead optimization won't affect the result. Choose a later date to optimize"
                )}
                style="info"
              />
            </div>
          )}
          <ScenarioOptimizationPeriodInfo subtypes={optimizationSubtypes} />
        </div>
      )}

      {finishedOptJobsLoaded &&
        (uiConfigId === null ? (
          <UiConfig
            type="day_ahead_bid_management"
            datasetStartTime={datasetStartTime}
            datasetEndTime={datasetEndTime}
            datasetRefreshToken={ELPLAN_BID_REFRESH_EVENT_QUERY_KEY}
            overrideAlias={resultAliases}
          />
        ) : (
          <UiConfigComponent
            systemId={systemId}
            id={uiConfigId}
            uid={idToUiConfig[uiConfigId]?.uid}
            datasetStartTime={datasetStartTime}
            datasetEndTime={datasetEndTime}
            overrideAlias={resultAliases}
            datasetRefreshToken={ELPLAN_BID_REFRESH_EVENT_QUERY_KEY}
          />
        ))}

      <DayAheadBidInputSurface
        partitionId={bidEditorPartitionId}
        startTime={datasetStartTime}
        endTime={datasetEndTime}
        requireTagForBlockBids={requireTagForBlockBids ?? false}
        tableContextKey={tableContextKey}
        tableResetController={tableResetController}
      >
        <DayAheadBidEditor
          filteredBidTypeTypes={scenario.bid_types}
          datasetStartTime={datasetStartTime}
          datasetEndTime={datasetEndTime}
          tagsForDate={tagsForDate}
          bidInputCurrencies={bidInputCurrencies}
          requireTagForBlockBids={requireTagForBlockBids}
          setCurrentBlockBid={setCurrentBlockBid}
          setCurrentProfileBid={setCurrentProfileBid}
          setTypeOfBidToCopy={setTypeOfBidToCopy}
          disabledBidButtons={disabledBidButtons}
          scenarioId={scenario.id}
          partitionId={bidEditorPartitionId}
          regularPlanSubtypeAliases={regularPlanSubtypeAliases}
          showAllScenarioBids={scenario.all_bids}
        />
      </DayAheadBidInputSurface>
    </>
  )
}
